<script setup>
import { useTheme } from 'vuetify';
import { useRoute, useRouter } from 'vue-router';
import ScrollToTop from '@core/components/ScrollToTop.vue';
import { initConfigStore, useConfigStore } from '@core/stores/config';
import { hexToRgb } from '@layouts/utils';
import axios from 'axios';
import { onMounted, onBeforeUnmount, ref, watch } from 'vue';
import GuestApi from '@/api/GuestApi';
import { getXID, getClientType } from '@/api/interceptor/interceptor';
import { DEV_MODE } from '@/config/dev-mode';
import { initRnMessage } from '@/composables/useRnMessageGlobal';
import { useLoginStore } from '@/store/modules/login';
import { useClientType } from '@/composables/useClientType';
import ConfirmComponent from '@/components/common/Confirm.vue';

const { global } = useTheme();

axios.defaults.baseURL = import.meta.env.VITE_APP_GW_URL;

initConfigStore();

const configStore = useConfigStore();
const router = useRouter();
const route = useRoute();

// @unhead/vue: title 기본 템플릿 + 라우트 meta.title 자동 반영
useHead({
  titleTemplate: (title) =>
    title ? `${title} | 낭만만세력` : '낭만만세력 - 자연의 이치를 담은 천문역법 서비스',
});

useHead(() => ({
  title: route.meta?.title || null,
}));

// 앱(WebView) 글로벌 메시지 핸들러 초기화
if (window.ReactNativeWebView) {
  initRnMessage(router);
}

const initGuestUser = async () => {
  const isFirstVisit = !localStorage.getItem('xid');

  const xid = await getXID();
  const clientType = getClientType();

  // 테스트용: xid 확인 로그
  if (DEV_MODE) {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        action: 'debug',
        payload: { message: `[initGuestUser] xid=${xid}, isFirstVisit=${isFirstVisit}, clientType=${clientType}` },
      }));
    } else {
      console.log(`[initGuestUser] xid=${xid}, isFirstVisit=${isFirstVisit}, clientType=${clientType}`);
    }
  }

  if (isFirstVisit) {
    try {
      await GuestApi.addGuestUser(xid, clientType);
    } catch (e) {
      // 400: 이미 등록된 게스트 — 무시하고 get 진행
    }
  }

  try {
    const { data } = await GuestApi.getGuestUser(xid);
    if (data?.guestInfoId) {
      sessionStorage.setItem('guestInfoId', data.guestInfoId);
    }
  } catch (e) {
    console.error('게스트 사용자 조회 실패:', e);
  }
};

// =============== 로그인 모달 (글로벌) ===============
const loginStore = useLoginStore();
const loginConfirm = ref(null);

const confirmLogin = () => {
  if (window.ReactNativeWebView) {
    // 앱: 네비게이션 차단 + 로그인 화면 이동 (로그인 후 다시 접근)
    loginStore.handleLoginCancel();
    window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'login' }));
  } else {
    const redirect = loginStore.loginRedirect || router.currentRoute.value.fullPath;
    loginStore.handleLoginConfirm();
    router.push({ path: '/auth/login', query: { to: redirect } });
  }
};

watch(
  () => loginStore.isLoginModalVisible,
  (newState) => {
    if (newState) loginConfirm.value?.openConfirmDialog();
  },
);

// =============== Safe Area CSS 변수 주입 ===============
const { safeAreaInsets, isMobileUi } = useClientType();

watch(
  [() => safeAreaInsets.value, () => isMobileUi.value],
  ([insets]) => {
    document.documentElement.style.setProperty('--safe-area-top', `${insets.top}px`);
    document.documentElement.style.setProperty('--safe-area-bottom', `${insets.bottom}px`);
    if (window.ReactNativeWebView) {
      // 앱(WebView): 탭바 높이 + 앱이 주입한 하단 safe-area
      document.documentElement.style.setProperty('--mobile-tab-height', `${57 + insets.bottom}px`);
    } else if (isMobileUi.value) {
      // 모바일 웹: 탭바 높이(56px) + iOS 홈 인디케이터 safe-area(env)
      document.documentElement.style.setProperty(
        '--mobile-tab-height',
        'calc(56px + env(safe-area-inset-bottom, 0px))',
      );
    } else {
      // PC 웹: 탭바 없음
      document.documentElement.style.removeProperty('--mobile-tab-height');
    }
  },
  { immediate: true },
);

// =============== 앱(WebView) 환경 감지 및 body 클래스 추가 (전역 처리) ===============
onMounted(() => {
  if (typeof window !== 'undefined' && window.ReactNativeWebView) {
    document.body.classList.add('is-webview');
  }

  // 앱(WebView)에서는 게스트 등록/조회를 앱이 이미 처리하므로 스킵.
  // guestInfoId는 init 메시지로 전달받아 useRnMessageGlobal에서 sessionStorage에 저장.
  if (!window.ReactNativeWebView) {
    initGuestUser();
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined' && document.body.classList.contains('is-webview')) {
    document.body.classList.remove('is-webview');
  }
});
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <RouterView />
      <ScrollToTop />
      <Teleport to="body">
        <ConfirmComponent
          ref="loginConfirm"
          :title="'로그인 후 이용가능합니다.'"
          :text="'로그인'"
          :nextUrl="confirmLogin"
          @cancel="loginStore.handleLoginCancel"
        />
      </Teleport>
    </VApp>
  </VLocaleProvider>
</template>
